import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Megaphone,
  Sparkles,
  AlertTriangle,
  PartyPopper,
  TicketPercent,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  MapPin,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  getSystemAnnouncementStyle,
  getSystemAnnouncementStyleLabel,
} from '@/constants/systemAnnouncementStyles';
import '@/styles/ipe/workbench/modals/SystemAnnouncementPopup.css';

const TITLE_ID = 'ipe-system-announcement-popup-title';

const STYLE_ICONS = {
  info: Megaphone,
  update: Sparkles,
  warning: AlertTriangle,
  success: PartyPopper,
  promo: TicketPercent,
  activity: CalendarDays,
};

function resolveStepImages(step) {
  if (Array.isArray(step?.images) && step.images.length > 0) {
    return step.images
      .map((item) => {
        if (typeof item === 'string') return item;
        return String(item?.imageUrl || '').trim();
      })
      .filter(Boolean);
  }
  const single = String(step?.imageUrl || '').trim();
  return single ? [single] : [];
}

function resolveSteps(announcement) {
  if (Array.isArray(announcement?.steps) && announcement.steps.length > 0) {
    return announcement.steps;
  }
  return [{
    title: '',
    message: announcement?.message || '',
    images: [],
    imageUrl: '',
    actionLabel: '',
    actionUrl: '',
  }];
}

/**
 * 系统公告弹窗。
 * mode=preview：管理端居中预览；mode=tour：用户端右下角指引并定位功能页。
 */
export default function SystemAnnouncementPopup({
  open,
  announcement,
  onDismiss,
  onAction,
  onGuideLocate,
  mode = 'preview',
}) {
  const { t } = useTranslation('workbench');
  const [stepIndex, setStepIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);
  const isTour = mode === 'tour';

  const steps = useMemo(
    () => (announcement ? resolveSteps(announcement) : []),
    [announcement],
  );

  useEffect(() => {
    if (open) {
      setStepIndex(0);
      setImageIndex(0);
    }
  }, [open, announcement?.id]);

  useEffect(() => {
    setImageIndex(0);
  }, [stepIndex]);

  useEffect(() => {
    if (!open || !isTour || !steps.length || typeof onGuideLocate !== 'function') return;
    const step = steps[Math.min(stepIndex, steps.length - 1)];
    onGuideLocate(step, {
      stepIndex,
      total: steps.length,
      announcement,
    });
  }, [open, isTour, stepIndex, steps, announcement, onGuideLocate]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onDismiss?.();
        return;
      }
      const step = steps[Math.min(stepIndex, Math.max(0, steps.length - 1))] || {};
      const images = resolveStepImages(step);
      if (event.key === 'ArrowRight') {
        if (images.length > 1 && imageIndex < images.length - 1) {
          setImageIndex((v) => Math.min(images.length - 1, v + 1));
          return;
        }
        if (stepIndex < steps.length - 1) {
          setStepIndex((v) => Math.min(steps.length - 1, v + 1));
        }
      }
      if (event.key === 'ArrowLeft') {
        if (images.length > 1 && imageIndex > 0) {
          setImageIndex((v) => Math.max(0, v - 1));
          return;
        }
        if (stepIndex > 0) {
          setStepIndex((v) => Math.max(0, v - 1));
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onDismiss, stepIndex, imageIndex, steps]);

  if (!open || !announcement) return null;

  const styleMeta = getSystemAnnouncementStyle(announcement.style);
  const Icon = STYLE_ICONS[styleMeta.value] || Megaphone;
  const styleClass = `ipe-sys-announce--${styleMeta.value}`;
  const isUpdate = styleMeta.value === 'update';
  const total = steps.length;
  const safeIndex = Math.min(stepIndex, Math.max(0, total - 1));
  const step = steps[safeIndex] || {};
  const images = resolveStepImages(step);
  const safeImageIndex = Math.min(imageIndex, Math.max(0, images.length - 1));
  const imageUrl = images[safeImageIndex] || '';
  const isFirst = safeIndex <= 0;
  const isLast = safeIndex >= total - 1;
  const guideLabel = String(step.actionLabel || '').trim();
  const guideUrl = String(step.actionUrl || '').trim();
  const globalActionUrl = String(announcement.actionUrl || '').trim();
  const globalActionLabel = String(announcement.actionLabel || '').trim()
    || (isUpdate ? t('systemAnnouncement.actionUpdate') : t('systemAnnouncement.actionDetails'));
  const stepTitle = String(step.title || '').trim();
  const stepMessage = String(step.message || '').trim();

  const handleFinish = () => {
    if (globalActionUrl) {
      onAction?.({
        ...announcement,
        actionUrl: globalActionUrl,
        actionLabel: globalActionLabel,
      });
      return;
    }
    onDismiss?.();
  };

  return createPortal(
    <div
      className={[
        'ipe-sys-announce',
        isTour ? 'ipe-sys-announce--touring' : 'ipe-sys-announce--preview',
        styleClass,
        isUpdate ? 'ipe-sys-announce--guide' : '',
      ].filter(Boolean).join(' ')}
      role="presentation"
    >
      <div className="ipe-sys-announce__backdrop" role="presentation" onClick={onDismiss} />
      <div
        className="ipe-sys-announce__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="ipe-sys-announce__banner" aria-hidden="true">
          <span className="ipe-sys-announce__banner-glow" />
          <span className="ipe-sys-announce__banner-orb ipe-sys-announce__banner-orb--a" />
          <span className="ipe-sys-announce__banner-orb ipe-sys-announce__banner-orb--b" />
        </div>
        <button
          type="button"
          className="ipe-sys-announce__close"
          aria-label={t('commonActions.close')}
          onClick={onDismiss}
        >
          <X size={18} strokeWidth={2.1} />
        </button>
        <div className="ipe-sys-announce__body">
          <div className="ipe-sys-announce__hero">
            <span className="ipe-sys-announce__icon" aria-hidden="true">
              <Icon size={24} strokeWidth={2.05} />
            </span>
            <div className="ipe-sys-announce__copy">
              <div className="ipe-sys-announce__meta">
                <span className="ipe-sys-announce__badge">{getSystemAnnouncementStyleLabel(styleMeta.value)}</span>
                {total > 1 ? (
                  <span className="ipe-sys-announce__pager">
                    {safeIndex + 1} / {total}
                  </span>
                ) : null}
              </div>
              <h3 id={TITLE_ID} className="ipe-sys-announce__title">{announcement.title}</h3>
              {stepTitle ? (
                <h4 className="ipe-sys-announce__step-title">{stepTitle}</h4>
              ) : null}
            </div>
          </div>

          {(guideLabel || guideUrl) ? (
            <div className="ipe-sys-announce__locate" aria-live="polite">
              <MapPin size={14} strokeWidth={2.2} aria-hidden="true" />
              <span>
                {guideLabel
                  ? t(isTour ? 'systemAnnouncement.locatedAt' : 'systemAnnouncement.willLocateAt', { label: guideLabel })
                  : t(isTour ? 'systemAnnouncement.locatedStep' : 'systemAnnouncement.willLocateStep')}
              </span>
            </div>
          ) : null}

          {imageUrl ? (
            <div className="ipe-sys-announce__media">
              <img src={imageUrl} alt={stepTitle || announcement.title || t('systemAnnouncement.imageAltFallback')} />
              {images.length > 1 ? (
                <>
                  <button
                    type="button"
                    className="ipe-sys-announce__media-nav ipe-sys-announce__media-nav--prev"
                    onClick={() => setImageIndex((v) => Math.max(0, v - 1))}
                    disabled={safeImageIndex <= 0}
                    aria-label={t('systemAnnouncement.prevImage')}
                  >
                    <ChevronLeft size={18} strokeWidth={2.2} />
                  </button>
                  <button
                    type="button"
                    className="ipe-sys-announce__media-nav ipe-sys-announce__media-nav--next"
                    onClick={() => setImageIndex((v) => Math.min(images.length - 1, v + 1))}
                    disabled={safeImageIndex >= images.length - 1}
                    aria-label={t('systemAnnouncement.nextImage')}
                  >
                    <ChevronRight size={18} strokeWidth={2.2} />
                  </button>
                  <div className="ipe-sys-announce__media-pager" aria-hidden="true">
                    {safeImageIndex + 1} / {images.length}
                  </div>
                </>
              ) : null}
            </div>
          ) : null}

          {stepMessage ? (
            <p className="ipe-sys-announce__message">{stepMessage}</p>
          ) : null}

          {total > 1 ? (
            <div className="ipe-sys-announce__dots" aria-hidden="true">
              {steps.map((_, idx) => (
                <button
                  key={`dot-${idx}`}
                  type="button"
                  className={`ipe-sys-announce__dot${idx === safeIndex ? ' is-active' : ''}`}
                  onClick={() => setStepIndex(idx)}
                  aria-label={t('systemAnnouncement.stepAria', { index: idx + 1 })}
                />
              ))}
            </div>
          ) : null}

          <footer className="ipe-sys-announce__footer">
            {total > 1 && !isFirst ? (
              <button
                type="button"
                className="ipe-sys-announce__btn ipe-sys-announce__btn--ghost"
                onClick={() => setStepIndex((v) => Math.max(0, v - 1))}
              >
                <ChevronLeft size={16} strokeWidth={2.2} aria-hidden="true" />
                {t('systemAnnouncement.prevStep')}
              </button>
            ) : (
              <button type="button" className="ipe-sys-announce__btn ipe-sys-announce__btn--ghost" onClick={onDismiss}>
                {isLast ? t('systemAnnouncement.skip') : t('commonActions.close')}
              </button>
            )}

            <div className="ipe-sys-announce__footer-right">
              {!isLast ? (
                <button
                  type="button"
                  className="ipe-sys-announce__btn ipe-sys-announce__btn--primary"
                  onClick={() => setStepIndex((v) => Math.min(total - 1, v + 1))}
                >
                  {t('systemAnnouncement.nextStep')}
                  <ChevronRight size={16} strokeWidth={2.2} aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="button"
                  className="ipe-sys-announce__btn ipe-sys-announce__btn--primary"
                  onClick={handleFinish}
                >
                  {globalActionUrl ? globalActionLabel : t('systemAnnouncement.finish')}
                </button>
              )}
            </div>
          </footer>
        </div>
      </div>
    </div>,
    document.body,
  );
}
